import React, { useState } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Phone, Mail, MapPin, MessageSquare, Send, Clock, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { COMPANY } from '../mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', message: '' });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email || !form.message) {
      return toast({ title: 'Please complete all required fields' });
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast({ title: 'Message sent!', description: "We'll respond within 24 business hours." });
      setForm({ firstName: '', lastName: '', email: '', message: '' });
    } catch (err) {
      console.error(err);
      toast({ title: 'Could not send', description: err?.response?.data?.detail || 'Please try again or call us directly.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <main>
        <section className="section bg-[#F7F9FC]">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
              {/* Left */}
              <div className="lg:col-span-2">
                <span className="text-xs font-bold uppercase tracking-[0.25em] gold-text">Contact us</span>
                <h1 className="mt-3 text-4xl md:text-5xl font-extrabold navy-text leading-tight">We’re here to help — every step of the way.</h1>
                <p className="mt-4 text-[#4A5A72] text-lg">
                  Whether you need a quote, have a question about your shipment, or want to learn more about our services — our team is ready to assist.
                </p>
                <div className="mt-8 space-y-4">
                  <a href={COMPANY.phoneHref} className="flex items-start gap-4 p-4 bg-white rounded-xl border border-[#E5EAF2] hover:border-[#D4A24C] transition-colors">
                    <div className="w-11 h-11 rounded-lg bg-[#0A1628] flex items-center justify-center shrink-0">
                      <Phone className="w-5 h-5 text-[#D4A24C]" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wider text-[#4A5A72]">Call us</div>
                      <div className="font-extrabold navy-text">{COMPANY.phone}</div>
                    </div>
                  </a>
                  <a href={COMPANY.emailHref} className="flex items-start gap-4 p-4 bg-white rounded-xl border border-[#E5EAF2] hover:border-[#D4A24C] transition-colors">
                    <div className="w-11 h-11 rounded-lg bg-[#0A1628] flex items-center justify-center shrink-0">
                      <Mail className="w-5 h-5 text-[#D4A24C]" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wider text-[#4A5A72]">Email</div>
                      <div className="font-extrabold navy-text break-all">{COMPANY.email}</div>
                    </div>
                  </a>
                  <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-[#E5EAF2]">
                    <div className="w-11 h-11 rounded-lg bg-[#0A1628] flex items-center justify-center shrink-0">
                      <MapPin className="w-5 h-5 text-[#D4A24C]" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wider text-[#4A5A72]">Headquarters</div>
                      <div className="font-extrabold navy-text">New York, USA</div>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 bg-white rounded-xl border border-[#E5EAF2]">
                    <div className="w-11 h-11 rounded-lg bg-[#0A1628] flex items-center justify-center shrink-0">
                      <Clock className="w-5 h-5 text-[#D4A24C]" />
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wider text-[#4A5A72]">Hours</div>
                      <div className="font-extrabold navy-text">Mon–Sat · 8am–8pm ET</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form */}
              <div className="lg:col-span-3">
                <form onSubmit={submit} className="bg-white rounded-2xl p-6 md:p-8 border border-[#E5EAF2] shadow-sm">
                  <div className="flex items-center gap-2 mb-6">
                    <MessageSquare className="w-5 h-5 gold-text" />
                    <h2 className="text-xl font-extrabold navy-text">Send us a message</h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>First name <span className="text-[#D4A24C]">*</span></Label>
                      <Input className="mt-1.5 h-11" value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} />
                    </div>
                    <div>
                      <Label>Last name <span className="text-[#D4A24C]">*</span></Label>
                      <Input className="mt-1.5 h-11" value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} />
                    </div>
                  </div>
                  <div className="mt-4">
                    <Label>Email <span className="text-[#D4A24C]">*</span></Label>
                    <Input type="email" className="mt-1.5 h-11" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                  </div>
                  <div className="mt-4">
                    <Label>Message <span className="text-[#D4A24C]">*</span></Label>
                    <Textarea rows={6} className="mt-1.5" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} />
                  </div>
                  <Button type="submit" disabled={loading} className="btn-primary mt-6 font-bold h-12 w-full md:w-auto px-8 gap-2">
                    {loading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>) : (<>Submit <Send className="w-4 h-4" /></>)}
                  </Button>
                  <p className="text-xs text-[#4A5A72] mt-4">Protected by reCAPTCHA. By submitting, you agree to our Privacy Policy.</p>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default Contact;
